import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import useContentfulLocale from 'components/useContentfulLocale';
import ctf from 'utils/contentfulService';
import { isClientAccepted } from 'utils/analyzeClientResource';

export const homeIntroStyles = {
  left: 'image on left',
  right: 'image on right',
  center: 'image centered',
};

// Fetch reference page ID by content type and slug using Contentful JS SDK
const fetchReferencePageId = async ({ contentType, slug }) => {
  const response = await ctf.getEntries({
    content_type: contentType,
    'fields.slug': slug,
    select: 'sys.id',
    limit: 1,
  });

  return response.items[0]?.sys?.id || null;
};

// Custom hook to fetch and cache reference page ID
const useReferencePageId = ({ contentType, slug, options = {} }) =>
  useQuery(
    contentType && slug ? ['referencePageId', contentType, slug] : null,
    () => fetchReferencePageId({ contentType, slug }),
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      ...options,
    },
  );

const getIntros = async (
  _key,
  { type, page, tags, home, filters, referenceId },
) => {
  const ctfPayload = {
    content_type: 'intros',
    select:
      'fields.referencePages,fields.client,fields.excludeClient,fields.commonPages,fields.header,fields.description,fields.url,fields.linkName,fields.style,fields.image,fields.multipleImages,fields.tags,fields.context',
    'fields.reviewStatus': 'Accepted',
    limit: 1000,
    ...filters,
    ...(type === 'reference' && referenceId
      ? { 'fields.referencePages.sys.id': referenceId }
      : {}),
    ...(home
      ? { 'fields.style[in]': Object.values(homeIntroStyles).join(',') }
      : {}),
    ...(type === 'common' && page ? { 'fields.commonPages': page } : {}),
    ...(type === 'common' && Array.isArray(tags) && tags.length > 0
      ? { 'fields.tags.sys.id[in]': tags.map(tag => tag.id).join(',') }
      : {}),
  };

  const response = await ctf.getEntries(ctfPayload);
  return response.items;
};

const useIntros = ({
  type,
  referenceId,
  referenceSlug,
  page,
  tags,
  home,
  queryOptions = {},
} = {}) => {
  const clientDetails = useSelector(makeSelectClientDetails());
  const localeFilters = useContentfulLocale();

  const clientId = _get(clientDetails, 'sys.id');

  // Exclude client filter
  const excludeClientFilter = clientId
    ? { 'fields.excludeClient.sys.id[ne]': clientId }
    : {};

  // Compose all filters
  const filters = {
    ...excludeClientFilter,
    ...localeFilters,
  };

  // Reference page ID lookup
  const refIdQuery = useReferencePageId({
    contentType: type === 'reference' && !referenceId ? page : undefined,
    slug: type === 'reference' && !referenceId ? referenceSlug : undefined,
  });

  let refId = null;
  let enabled = true;

  if (type === 'reference') {
    if (referenceId) {
      refId = referenceId;
    } else if (page && referenceSlug) {
      refId = refIdQuery.data;
      enabled = !!refId && !refIdQuery.isLoading && !refIdQuery.error;
    } else {
      enabled = false;
    }
  }

  const result = useQuery(
    enabled &&
      clientId && [
        'intros',
        { type, referenceId: refId, referenceSlug, page, tags, home, filters },
      ],
    getIntros,
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      ...queryOptions,
    },
  );

  const { data: intros } = result;

  const finalIntros = useMemo(
    () =>
      intros
        ?.filter(item => {
          const isAccepted = isClientAccepted(item, clientDetails);
          return isAccepted;
        })
        .sort(
          (a, b) =>
            _get(b, 'fields.client', []).length -
            _get(a, 'fields.client', []).length,
        ),
    [intros, clientDetails],
  );

  return {
    ...result,
    data: finalIntros,
  };
};

export default useIntros;
